import { ApiResponse } from '@/types/apiResponse';
import {
  type FeedCard,
  FeedCardType,
  type FeedComment,
  type FeedCommentsPage,
  type FeedCtaAction,
  type FeedConfig,
  type FeedLikeState,
  type FeedLikesPage,
  type FeedPage,
  type FeedReaction,
  type FeedShareChannel,
  type FeedSponsoredOptIn,
} from '@/types/feed';
import { currentSessionId } from '@/analytics/eventTracker';
import { apiService } from '../api';

const PUBLIC_BASE = '/public/api/v1/feed';
const PRIVATE_BASE = '/private/api/v1/feed';

/**
 * The analytics session this action happened in, so the server's like / comment / share event
 * joins that session (epic 21: engaged openers). Omitted when there is none, never sent as "".
 */
const withSession = <T extends object>(body: T): T & { sessionId?: string } => {
  const sessionId = currentSessionId();
  return sessionId ? { ...body, sessionId } : body;
};

/** The viewer's IANA zone, so dates in a caption read in local time. Falls back to the server default. */
const viewerTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const postPath = (postId: string) => `/posts/${encodeURIComponent(postId)}`;

/** The read params every feed request shares: the viewer's zone, and the brand-ad opt-in when on. */
const readParams = (
  sponsored: FeedSponsoredOptIn | null | undefined,
): Record<string, string> => {
  const params: Record<string, string> = {};
  const tz = viewerTimezone();
  if (tz) params.tz = tz;
  if (sponsored) {
    params.cardTypes = FeedCardType.SPONSORED;
    params.platform = 'web';
    if (sponsored.anonId) params.anonId = sponsored.anonId;
    if (sponsored.lang) params.lang = sponsored.lang;
  }
  return params;
};

class FeedService {
  /** The global feed, newest first (GitLab epic 17). Public; personal (likes, CTAs) when logged in. */
  async getFeed(
    params: {
      cursor?: string;
      limit?: number;
      /** Brand ads opt-in; omit and the server never sends a sponsored card. */
      sponsored?: FeedSponsoredOptIn | null;
    } = {},
  ): Promise<ApiResponse<FeedPage>> {
    const query: Record<string, string | number> = {
      limit: params.limit ?? 10,
      ...readParams(params.sponsored),
    };
    if (params.cursor) query.cursor = params.cursor;
    return apiService.get<ApiResponse<FeedPage>>(PUBLIC_BASE, {
      params: query,
    });
  }

  /**
   * One shared post. Public: works logged out. A brand ad's link only renders with the
   * opt-in; without it the server answers 404.
   */
  async getFeedPost(
    postId: string,
    sponsored?: FeedSponsoredOptIn | null,
  ): Promise<ApiResponse<FeedCard>> {
    const params = readParams(sponsored);
    return apiService.get<ApiResponse<FeedCard>>(
      `${PUBLIC_BASE}${postPath(postId)}`,
      Object.keys(params).length > 0 ? { params } : undefined,
    );
  }

  /** React, or switch reaction — one per user per post. */
  async react(
    postId: string,
    reaction: FeedReaction,
  ): Promise<ApiResponse<FeedLikeState>> {
    return apiService.put<
      ApiResponse<FeedLikeState>,
      { reaction: FeedReaction; sessionId?: string }
    >(`${PRIVATE_BASE}${postPath(postId)}/reaction`, withSession({ reaction }));
  }

  async removeReaction(postId: string): Promise<ApiResponse<FeedLikeState>> {
    return apiService.delete<ApiResponse<FeedLikeState>>(
      `${PRIVATE_BASE}${postPath(postId)}/reaction`,
    );
  }

  /** Enabled reactions, in picker order. */
  async getConfig(): Promise<ApiResponse<FeedConfig>> {
    return apiService.get<ApiResponse<FeedConfig>>(`${PUBLIC_BASE}/config`);
  }

  /** Who reacted: newest first. */
  async getLikes(
    postId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<FeedLikesPage>> {
    const query: Record<string, string | number> = {
      limit: params.limit ?? 20,
    };
    if (params.cursor) query.cursor = params.cursor;
    return apiService.get<ApiResponse<FeedLikesPage>>(
      `${PUBLIC_BASE}${postPath(postId)}/likes`,
      { params: query },
    );
  }

  /** Oldest first. */
  async getComments(
    postId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResponse<FeedCommentsPage>> {
    const query: Record<string, string | number> = {
      limit: params.limit ?? 20,
    };
    if (params.cursor) query.cursor = params.cursor;
    return apiService.get<ApiResponse<FeedCommentsPage>>(
      `${PUBLIC_BASE}${postPath(postId)}/comments`,
      { params: query },
    );
  }

  async addComment(
    postId: string,
    text: string,
  ): Promise<ApiResponse<{ comment: FeedComment; count: number }>> {
    return apiService.post<
      ApiResponse<{ comment: FeedComment; count: number }>,
      { text: string; sessionId?: string }
    >(`${PRIVATE_BASE}${postPath(postId)}/comments`, withSession({ text }));
  }

  async deleteComment(
    postId: string,
    commentId: string,
  ): Promise<ApiResponse<{ count: number }>> {
    return apiService.delete<ApiResponse<{ count: number }>>(
      `${PRIVATE_BASE}${postPath(postId)}/comments/${encodeURIComponent(commentId)}`,
    );
  }

  /** Counted when the user picked a share target; the browser never says whether they finished. */
  async recordShare(
    postId: string,
    channel: FeedShareChannel,
  ): Promise<ApiResponse<{ count: number }>> {
    return apiService.post<
      ApiResponse<{ count: number }>,
      { channel: FeedShareChannel; sessionId?: string }
    >(`${PUBLIC_BASE}${postPath(postId)}/share`, withSession({ channel }));
  }

  /** A CTA tap on a feed card, for registration attribution (epic 21 #1970). Logged in only. */
  async recordCtaTap(
    postId: string,
    action: FeedCtaAction,
  ): Promise<ApiResponse<{ recorded: boolean }>> {
    return apiService.post<
      ApiResponse<{ recorded: boolean }>,
      { action: FeedCtaAction }
    >(`${PRIVATE_BASE}${postPath(postId)}/cta-tap`, { action });
  }
}

export const feedService = new FeedService();
